import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  createAntiplag, createDefense, createTopic, finalizeDefense,
  listDefenses, listTheses, listTopics, myThesis, updateTopic, uploadVersion,
} from "service/DiplomaService";
import { listEmployees } from "service/HRService";
import Notification from "utils/Notifications";
import s from "./DiplomaPage.module.scss";

export default function DiplomaPage() {
  const { t } = useTranslation('common');
  const auth = useSelector((st) => st.auth);
  const codes = (auth?.roles || []).map((r) => (typeof r === "string" ? r : r?.code));
  const isStaff = codes.some((c) => ["methodist", "dean", "head_of_department", "rectorate", "sysadmin"].includes(c));
  const isStudent = codes.includes("student");

  const [tab, setTab] = useState(isStaff ? "topics" : "my");
  const [topics, setTopics] = useState([]);
  const [theses, setTheses] = useState([]);
  const [my, setMy] = useState(null);
  const [defenses, setDefenses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [editingTopic, setEditingTopic] = useState(null);
  const [versionFor, setVersionFor] = useState(null);
  const [antiplagFor, setAntiplagFor] = useState(null);
  const [defenseFor, setDefenseFor] = useState(null);
  const [notify, setNotify] = useState({ isOpen: false });

  const reload = () => {
    if (isStaff) {
      listTopics().then((r) => setTopics(r.data?.results || r.data || []));
      listTheses().then((r) => setTheses(r.data?.results || r.data || []));
      listDefenses().then((r) => setDefenses(r.data?.results || r.data || []));
      listEmployees({ status: "active", page_size: 200 })
        .then((r) => setTeachers(r.data?.results || r.data || []));
    }
    if (isStudent) myThesis().then((r) => setMy(r.data));
  };
  useEffect(() => { reload();  }, []);

  const saveTopic = async () => {
    try {
      if (editingTopic.id) await updateTopic(editingTopic.id, editingTopic);
      else await createTopic(editingTopic);
      setEditingTopic(null); reload();
    } catch { setNotify({ isOpen: true, message: t('common.error'), type: "error" }); }
  };

  const submitVersion = async () => {
    try {
      await uploadVersion({ thesis: versionFor.thesis, file: versionFor.file, summary: versionFor.summary });
      setVersionFor(null); reload();
      setNotify({ isOpen: true, message: t('diploma.version_uploaded'), type: "success" });
    } catch { setNotify({ isOpen: true, message: t('common.error'), type: "error" }); }
  };
  const submitAntiplag = async () => {
    try {
      await createAntiplag(antiplagFor);
      setAntiplagFor(null); reload();
    } catch { setNotify({ isOpen: true, message: t('common.error'), type: "error" }); }
  };
  const submitDefense = async () => {
    try {
      await createDefense(defenseFor);
      setDefenseFor(null); reload();
    } catch { setNotify({ isOpen: true, message: t('common.error'), type: "error" }); }
  };

  return (
    <div className={s.page}>
      <div className={s.toolbar}>
        <h2 className={s.title}>{t('diploma.title_page')}</h2>
        <div className={s.tabs}>
          {isStaff && <button type="button" role="tab" aria-selected={tab === "topics"} className={`${s.tab} ${tab === "topics" ? s.active : ""}`} onClick={() => setTab("topics")}>{t('diploma.tab_topics')}</button>}
          {isStaff && <button type="button" role="tab" aria-selected={tab === "theses"} className={`${s.tab} ${tab === "theses" ? s.active : ""}`} onClick={() => setTab("theses")}>{t('diploma.tab_theses')}</button>}
          {isStaff && <button type="button" role="tab" aria-selected={tab === "defenses"} className={`${s.tab} ${tab === "defenses" ? s.active : ""}`} onClick={() => setTab("defenses")}>{t('diploma.tab_defenses')}</button>}
          {isStudent && <button type="button" role="tab" aria-selected={tab === "my"} className={`${s.tab} ${tab === "my" ? s.active : ""}`} onClick={() => setTab("my")}>{t('diploma.tab_my')}</button>}
        </div>
      </div>

      {tab === "topics" && isStaff && (
        <>
          <div className={s.toolbar}>
            <button className="unv-btn unv-btn--primary"
                    onClick={() => setEditingTopic({ year: new Date().getFullYear(), status: "available" })}>
              {t('diploma.add_topic')}
            </button>
          </div>
          <table className={s.table}>
            <thead><tr><th>{t('diploma.col_topic')}</th><th>{t('diploma.col_year')}</th><th>{t('diploma.col_direction')}</th><th>{t('diploma.col_department')}</th><th>{t('diploma.col_proposed_by')}</th><th>{t('diploma.col_status')}</th><th></th></tr></thead>
            <tbody>
              {topics.map((tp) => (
                <tr key={tp.id}>
                  <td>{tp.title}</td><td>{tp.year}</td>
                  <td>{tp.direction_name || "—"}</td><td>{tp.department_name || "—"}</td>
                  <td>{tp.proposed_by_label || "—"}</td><td>{tp.status_display}</td>
                  <td><button className={s.smallBtn} onClick={() => setEditingTopic(tp)}>{t('diploma.edit_short')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {tab === "theses" && isStaff && (
        <table className={s.table}>
          <thead><tr><th>{t('diploma.col_student')}</th><th>{t('diploma.col_topic')}</th><th>{t('diploma.col_supervisor')}</th><th>{t('diploma.col_status')}</th><th></th></tr></thead>
          <tbody>
            {theses.map((th) => (
              <tr key={th.id}>
                <td>{th.student_label}</td>
                <td>{th.topic_title || th.custom_title || "—"}</td>
                <td>{th.supervisor_label || "—"}</td>
                <td>{th.status_display}</td>
                <td>
                  <button className={s.smallBtn} onClick={() => setVersionFor({ thesis: th.id })}>{t('diploma.version_btn')}</button>
                  <button className={s.smallBtn} onClick={() => setAntiplagFor({ thesis: th.id })}>{t('diploma.antiplag_btn')}</button>
                  <button className={s.smallBtn} onClick={() => setDefenseFor({ thesis: th.id })}>{t('diploma.schedule_defense_btn')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === "defenses" && isStaff && (
        <table className={s.table}>
          <thead><tr><th>{t('diploma.col_date')}</th><th>{t('diploma.col_student')}</th><th>{t('diploma.col_topic')}</th><th>{t('diploma.col_grade')}</th><th>{t('diploma.col_minutes')}</th><th>{t('diploma.col_status')}</th><th></th></tr></thead>
          <tbody>
            {defenses.map((d) => (
              <tr key={d.id}>
                <td>{d.date}</td><td>{theses.find((tt) => tt.id === d.thesis)?.student_label || `#${d.thesis}`}</td>
                <td>{theses.find((tt) => tt.id === d.thesis)?.topic_title || "—"}</td>
                <td>{d.grade || "—"} {d.grade_letter || ""}</td>
                <td>{d.minutes_no || "—"}</td><td>{d.status_display}</td>
                <td>
                  {d.status === "scheduled" && (
                    <button className={s.smallBtn} onClick={async () => {
                      const grade = prompt(t('diploma.grade_prompt'));
                      if (!grade) return;
                      const minutes = prompt(t('diploma.minutes_prompt')) || "";
                      await finalizeDefense(d.id, { grade, minutes_no: minutes });
                      reload();
                    }}>{t('diploma.finish_btn')}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === "my" && isStudent && (
        my ? (
          <div className={s.thesis}>
            <h3>{my.topic_title || my.custom_title}</h3>
            <p>{t('diploma.supervisor_label')}: <b>{my.supervisor_label || "—"}</b></p>
            <p>{t('diploma.status_label')}: {my.status_display}</p>
            <p>{t('diploma.deadline_label')}: {my.deadline || "—"}</p>

            <div className={s.versions}>
              <h4>{t('diploma.versions_title')}</h4>
              {(my.versions || []).map((v) => (
                <div key={v.id} className={s.version}>
                  <span>v.{v.version_no} — {v.uploaded_at?.slice(0, 10)}</span>
                  {v.file && <a href={v.file} target="_blank" rel="noreferrer" className="unv-link">{t('diploma.download_btn')}</a>}
                </div>
              ))}
              <button className="unv-btn unv-btn--primary" style={{ marginTop: 8 }}
                      onClick={() => setVersionFor({ thesis: my.id })}>{t('diploma.upload_new_version')}</button>
            </div>
          </div>
        ) : (
          <div className={s.thesis}>
            <p>{t('diploma.no_thesis_assigned')}</p>
          </div>
        )
      )}

      {editingTopic && (
        <div className={s.overlay} onClick={() => setEditingTopic(null)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{editingTopic.id ? t('diploma.modal_edit_topic') : t('diploma.modal_new_topic')}</h3>
            <div className={s.field}><label>{t('diploma.field_topic')}</label>
              <input className="unv-input" value={editingTopic.title || ""}
                     onChange={(e) => setEditingTopic({ ...editingTopic, title: e.target.value })} /></div>
            <div className={s.field}><label>{t('diploma.field_description')}</label>
              <textarea className="unv-input" rows={3} value={editingTopic.description || ""}
                        onChange={(e) => setEditingTopic({ ...editingTopic, description: e.target.value })} /></div>
            <div className={s.field}><label>{t('diploma.field_year')}</label>
              <input type="number" className="unv-input" value={editingTopic.year || ""}
                     onChange={(e) => setEditingTopic({ ...editingTopic, year: Number(e.target.value) })} /></div>
            <div className={s.field}><label>{t('diploma.field_proposed_by')}</label>
              <select className="unv-input" value={editingTopic.proposed_by || ""}
                      onChange={(e) => setEditingTopic({ ...editingTopic, proposed_by: Number(e.target.value) })}>
                <option value="">—</option>
                {teachers.map((tt) => (<option key={tt.id} value={tt.id}>{tt.full_name || `${tt.surname} ${tt.first_name}`}</option>))}
              </select></div>
            <div className={s.actions}>
              <button className="unv-btn" onClick={() => setEditingTopic(null)}>{t('common.cancel')}</button>
              <button className="unv-btn unv-btn--primary" onClick={saveTopic}>{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      {versionFor && (
        <div className={s.overlay} onClick={() => setVersionFor(null)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('diploma.upload_version_title')}</h3>
            <div className={s.field}><label>{t('diploma.field_file')}</label>
              <input type="file" onChange={(e) => setVersionFor({ ...versionFor, file: e.target.files[0] })} /></div>
            <div className={s.field}><label>{t('diploma.field_change_summary')}</label>
              <textarea className="unv-input" rows={3} value={versionFor.summary || ""}
                        onChange={(e) => setVersionFor({ ...versionFor, summary: e.target.value })} /></div>
            <div className={s.actions}>
              <button className="unv-btn" onClick={() => setVersionFor(null)}>{t('common.cancel')}</button>
              <button className="unv-btn unv-btn--primary" onClick={submitVersion}>{t('diploma.upload_btn')}</button>
            </div>
          </div>
        </div>
      )}

      {antiplagFor && (
        <div className={s.overlay} onClick={() => setAntiplagFor(null)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('diploma.antiplag_title')}</h3>
            <div className={s.field}><label>{t('diploma.field_similarity_pct')}</label>
              <input type="number" step="0.01" className="unv-input" value={antiplagFor.similarity_percent || ""}
                     onChange={(e) => setAntiplagFor({ ...antiplagFor, similarity_percent: e.target.value })} /></div>
            <div className={s.field}><label>{t('diploma.field_originality_pct')}</label>
              <input type="number" step="0.01" className="unv-input" value={antiplagFor.originality_percent || ""}
                     onChange={(e) => setAntiplagFor({ ...antiplagFor, originality_percent: e.target.value })} /></div>
            <div className={s.field}><label>{t('diploma.field_pdf_report')}</label>
              <input type="file" onChange={(e) => setAntiplagFor({ ...antiplagFor, report_file: e.target.files[0] })} /></div>
            <div className={s.actions}>
              <button className="unv-btn" onClick={() => setAntiplagFor(null)}>{t('common.cancel')}</button>
              <button className="unv-btn unv-btn--primary" onClick={submitAntiplag}>{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      {defenseFor && (
        <div className={s.overlay} onClick={() => setDefenseFor(null)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('diploma.schedule_defense_title')}</h3>
            <div className={s.field}><label>{t('diploma.field_date')}</label>
              <input type="date" className="unv-input" value={defenseFor.date || ""}
                     onChange={(e) => setDefenseFor({ ...defenseFor, date: e.target.value })} /></div>
            <div className={s.field}><label>{t('diploma.field_time')}</label>
              <input type="time" className="unv-input" value={defenseFor.starts_at || ""}
                     onChange={(e) => setDefenseFor({ ...defenseFor, starts_at: e.target.value })} /></div>
            <div className={s.field}><label>{t('diploma.field_place')}</label>
              <input className="unv-input" value={defenseFor.place || ""}
                     onChange={(e) => setDefenseFor({ ...defenseFor, place: e.target.value })} /></div>
            <div className={s.actions}>
              <button className="unv-btn" onClick={() => setDefenseFor(null)}>{t('common.cancel')}</button>
              <button className="unv-btn unv-btn--primary" onClick={submitDefense}>{t('diploma.create_btn')}</button>
            </div>
          </div>
        </div>
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
}
